import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Brandmark } from '@/components/typography/Brandmark';
import { GoldLine } from '@/components/ornaments/GoldLine';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useScrollLock } from '@/lib/useScrollLock';
import { cx } from '@/lib/cx';
import { NAV_PRIMARY, WHATSAPP } from './nav.config';
import styles from './SiteHeader.module.css';

/**
 * Contacto directo por WhatsApp (peticion del cliente, 3-ago-2026).
 *
 * Vive en la BARRA, no dentro del drawer: en movil el drawer esta cerrado el
 * 99 % del tiempo y un contacto que hay que descubrir en dos pasos no es un
 * banner de contacto. Por debajo de 1024 se queda el glifo solo —el rotulo no
 * cabe junto a la marca y el boton de menu a 320 px— y el nombre accesible lo
 * pone `aria-label`, que no depende de que el texto se pinte.
 *
 * El glifo lleva el verde de WhatsApp y el resto de la capsula va en la paleta
 * del sitio: una capsula verde en una cabecera crema/navy se ve como un pegote
 * de otra web. Desviacion registrada en `docs/VISUAL_DEVIATIONS.md`.
 */
function WhatsAppCta() {
  return (
    <a
      className={styles.whatsapp}
      href={WHATSAPP.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Escribirnos por WhatsApp al ${WHATSAPP.display}`}
    >
      <svg className={styles.whatsappGlyph} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23a8.23 8.23 0 0 1 8.24 8.24c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.15.16-.29.18-.53.06-.25-.13-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.44.13-.14.17-.24.25-.41.09-.16.04-.3-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42h-.47c-.16 0-.43.06-.65.3-.22.25-.86.84-.86 2.05s.88 2.38 1 2.54c.13.17 1.73 2.63 4.19 3.69.58.25 1.04.4 1.4.51.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.22-.17-.47-.29Z"
        />
      </svg>
      <span className={styles.whatsappLabel}>WhatsApp</span>
    </a>
  );
}

/**
 * COMPARTIR, junto a WhatsApp (peticion del cliente, 17-sep-2026: «en cada ciudad,
 * junto al logo del WhatsApp, el logo de COMPARTIR»).
 *
 * En movil —que es donde se comparte— abre la hoja NATIVA del sistema
 * (`navigator.share`): WhatsApp, Instagram, Telegram, correo... sin cargar un solo
 * boton de red social ni un script de terceros. Donde no existe, copia el enlace
 * y lo dice. Si el visitante cierra la hoja sin elegir, el navegador rechaza la
 * promesa con `AbortError`: no es un error y no se trata como tal.
 *
 * Solo el glifo, sin rotulo, a TODOS los anchos: la barra ya va medida al pixel
 * contra la nav en linea (ver `.whatsappLabel` en el CSS) y un rotulo mas volveria
 * a pisar «CANCÚN / RIVIERA MAYA» a 1280.
 */
function ShareCta() {
  const [copiado, setCopiado] = useState(false);

  const compartir = async () => {
    /* Sin query ni ancla: el enlace compartido no arrastra los `utm_*` ni el
     * `fbclid` de quien comparte, que atribuirían su visita a otra campaña. */
    const url = window.location.origin + window.location.pathname;
    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
      } catch {
        /* Cerrar la hoja sin compartir rechaza con AbortError: no hay nada que hacer. */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2200);
    } catch {
      /* Sin portapapeles (contexto no seguro): no hay alternativa honesta que ofrecer. */
    }
  };

  return (
    <button
      type="button"
      className={cx(styles.whatsapp, styles.share)}
      onClick={compartir}
      aria-label="Compartir esta página"
    >
      <svg className={styles.shareGlyph} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11A2.99 2.99 0 1 0 15 5c0 .24.04.47.09.7L8.04 9.81A2.99 2.99 0 1 0 6 15c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65A2.92 2.92 0 1 0 18 16.08Z"
        />
      </svg>
      <span className={copiado ? styles.shareAviso : 'az-visually-hidden'} role="status" aria-live="polite">
        {copiado ? 'Enlace copiado' : ''}
      </span>
    </button>
  );
}

/**
 * Cabecera del sitio: navegacion en linea en escritorio, DRAWER ACCESIBLE en
 * movil.
 *
 * Contrato de accesibilidad del drawer (gate de Fase 1/2):
 *  - el boton declara `aria-expanded` y `aria-controls`;
 *  - al abrir, el foco entra en el panel y queda ATRAPADO dentro;
 *  - `Escape` cierra y el foco vuelve al boton;
 *  - el fondo no hace scroll mientras esta abierto;
 *  - navegar cierra el panel (si no, en movil te quedas con el velo puesto).
 */
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const location = useLocation();

  const close = useCallback(() => setOpen(false), []);

  // Cambiar de ruta cierra el drawer. Se ajusta DURANTE el render, no en un
  // efecto: asi no hay un frame intermedio con el panel abierto sobre la
  // pagina nueva. Cubre tambien atras/adelante del navegador, donde no pasa
  // por el `onClick` de los enlaces.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    if (open) setOpen(false);
  }

  useScrollLock(open);
  useFocusTrap(panelRef, open, close);

  // Transparente sobre el hero, solida al bajar. Los mockups de ciudad no
  // dibujan cabecera: esta es la formula que respeta el hero y a la vez deja
  // navegacion real (PAGE_CONTRACT §7, adicion registrada en VISUAL_DEVIATIONS).
  // Se escucha el scroll con `passive` y solo se toca el estado cuando el
  // umbral CAMBIA de lado, no en cada pixel.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const THRESHOLD = 24;
    const onScroll = () => {
      setScrolled((previous) => {
        const next = window.scrollY > THRESHOLD;
        return previous === next ? previous : next;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header className={cx(styles.header, scrolled && styles.headerSolid)} data-scrolled={scrolled}>
        <div className={styles.bar}>
          <Link className={styles.brandLink} to="/" aria-label="Ir a la portada de la gira">
            {/* El emblema de la cabecera esta sobre el pliegue en las 6 rutas. */}
            <Brandmark size="sm" eager />
          </Link>

          <nav className={styles.inlineNav} aria-label="Navegación principal">
            <ul className={styles.inlineList} role="list">
              {NAV_PRIMARY.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => cx(styles.navLink, isActive && styles.navLinkActive)}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* WhatsApp y el boton de menu van AGRUPADOS. Sueltos, el
              `space-between` de la barra reparte el hueco entre los tres y el
              contacto queda flotando en mitad de la cabecera, como si se
              hubiera caido ahi. */}
          <div className={styles.actions}>
            <WhatsAppCta />
            <ShareCta />

            <button
              type="button"
              className={styles.toggle}
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setOpen((value) => !value)}
            >
              <span className={styles.toggleBars} aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              <span className="az-visually-hidden">{open ? 'Cerrar el menú' : 'Abrir el menú'}</span>
            </button>
          </div>
        </div>

        <GoldLine width="full" ornament="none" className={styles.headerRule} />
      </header>

      {/* ⚠️ EL VELO Y EL PANEL VAN FUERA DE `<header>`, Y NO ES ORGANIZACIÓN: ES LA
          CORRECCIÓN DE UN DEFECTO MEDIDO EN PRODUCCIÓN.

          `.headerSolid` —el estado con scroll— lleva `backdrop-filter: blur(10px)`, y
          un `backdrop-filter` distinto de `none` convierte al elemento en BLOQUE
          CONTENEDOR de sus descendientes `position: fixed`, igual que `transform` o
          `filter`. Con el panel dentro de la cabecera, su `inset-block: 0` dejaba de
          medir el viewport y pasaba a medir LOS 61 px DE LA BARRA: el panel salía de
          390x61 y seis de los trece enlaces se pintaban por fuera, encima del texto de
          la página. Medido a 390 px abriendo el menú al 25 %, 50 % y 85 % de scroll;
          desde arriba del todo NO pasa, que es justo por qué ningún test lo cazaba y
          por qué el cliente lo describió como «a veces, cuando estás en x parte».

          Como hermanos funciona sin tocar nada más: `--az-z-drawer` (300) ya está por
          encima de `--az-z-header` (200). */}
      {open ? <div className={styles.scrim} onClick={close} aria-hidden="true" /> : null}

      <div
        id={panelId}
        ref={panelRef}
        className={cx(styles.drawer, open && styles.drawerOpen)}
        // `inert` saca del arbol de accesibilidad y del tab-order lo que no se ve.
        inert={!open}
        tabIndex={-1}
      >
        <nav aria-label="Navegación del sitio">
          <ul className={styles.drawerList} role="list">
            {NAV_PRIMARY.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => cx(styles.drawerLink, isActive && styles.drawerLinkActive)}
                  onClick={close}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <button type="button" className={styles.drawerClose} onClick={close}>
          Cerrar
        </button>
      </div>
    </>
  );
}
